import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Row } from 'read-excel-file';
import { Subject } from 'rxjs';
import { AccountFilterComponent, DateFilterComponent } from "../../../_shared/components";
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { NotificationIconComponent } from "../../../_shared/components/notification-icon/notification-icon.component";
import { FilterService } from '../../../_shared/services';
import { buildDateFilterFromPageFilter } from '../../../_shared/helpers/page-filter-state-helper';
import { DateInterval } from '../../../_shared/enums';
import { AccountFilterData, DateIntervalFilterData } from '../../../_shared/models';
import { WidgetFilterData } from '../../../_shared/models/filters/widget-filter-data.model';
import { PartsTabType } from '../../_shared/models/tabs.models';
import { InventoryValueWidgetComponent } from "./components/widgets/inventory-value-widget/inventory-value-widget.component";
import { LowStockWidgetComponent } from "./components/widgets/low-stock-widget/low-stock-widget.component";
import { OutOfStockWidgetComponent } from "./components/widgets/out-of-stock-widget/out-of-stock-widget.component";
import { TotalPartsWidgetComponent } from "./components/widgets/total-parts-widget/total-parts-widget.component";
import { InventoryOverviewComponent } from "./components/inventory-overview/inventory-overview.component";
import { PartsRequestOverviewComponent } from "./components/parts-request-overview/parts-request-overview.component";
import { PartsUsageOverviewComponent } from "./components/parts-usage-overview/parts-usage-overview.component";
import { AddPartComponent } from './components/modals/add-part/add-part.component';
import { ReturnPartsComponent } from './components/modals/return-parts/return-parts.component';
import { PartsReviewExcelModalComponent } from './components/modals/parts-review-excel-modal/parts-review-excel-modal.component';
import { DownloadTemplateModalComponent } from '../../_shared/components/modals/download-template-modal/download-template-modal.component';
import { readExcelFile } from '../../_shared/helpers/excel-upload.helper';
import { LookupService } from '../../_shared/services/lookup.service';
import { ErrorHandlerService } from '../../../_shared/services/error-handler.service';
import {
  convertRowsToPartExcelRows,
  DEFAULT_PARTS_EXCEL_COLUMN_SETTINGS,
  downloadPartsExcelTemplateAsync,
  stripPartsTemplateRows
} from './helpers/parts-upload.helper';
import { PartExcelRow } from './models/part-import.model';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-parts',
  standalone: true,
  imports: [
    CommonModule,
    InventoryValueWidgetComponent,
    AccountFilterComponent,
    DateFilterComponent,
    LowStockWidgetComponent,
    OutOfStockWidgetComponent,
    TotalPartsWidgetComponent,
    InventoryOverviewComponent,
    PartsRequestOverviewComponent,
    PartsUsageOverviewComponent,
    NotificationIconComponent,
    TranslocoDirective
  ],
  templateUrl: './parts.component.html',
  styleUrl: './parts.component.scss'
})
export class PartsComponent extends BasePageComponent implements OnInit, OnDestroy {
  private modalService: NgbModal = inject(NgbModal);
  private filterService: FilterService = inject(FilterService);
  private lookupService = inject(LookupService);
  private errorService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancellation = new Subject<void>();

  public dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  public accountFilter: AccountFilterData = {} as AccountFilterData;
  public defaultInterval: DateInterval = DateInterval.Month;
  public disabledIntervals: DateInterval[] = [DateInterval.Half];

  public notificationCount = 500;
  public selectedTab: PartsTabType = 'inventory';
  public isAccountFilterLoaded = false;
  public refreshInventoryVersion = 0;
  public isDownloadingTemplate = false;

  public driverModal: any = InventoryValueWidgetComponent;

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.syncFilterService();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancellation.next();
    this.cancellation.complete();
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean {
    return false;
  }

  protected override getFilters() {
    return {
      ...this.accountFilter,
      ...this.dateFilter,
      selectedTab: this.selectedTab
    };
  }

  protected override applyPageFilterState(pageFilter: any): void {
    this.accountFilter = { accountIds: pageFilter.accountIds ?? [] } as AccountFilterData;
    this.isAccountFilterLoaded = Array.isArray(this.accountFilter.accountIds);
    this.selectedTab = pageFilter.selectedTab ?? 'inventory';

    if (pageFilter.interval !== undefined || pageFilter.isCustomDate !== undefined) {
      this.dateFilter = buildDateFilterFromPageFilter(this.dateFilter, pageFilter);
    }
  }

  onTabChange(tab: PartsTabType): void {
    if (this.selectedTab === tab) {
      return;
    }

    this.selectedTab = tab;
    this.updatePageFilterState();
    this.syncFilterService();
  }

  isTabActive(tab: PartsTabType): boolean {
    return this.selectedTab === tab;
  }

  onPartsDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updatePageFilterState();
    this.syncFilterService();
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.isAccountFilterLoaded = Array.isArray(filter?.accountIds);

    if (this.isAccountFilterLoaded) {
      this.updatePageFilterState();
    }

    this.syncFilterService();
  }

  onAddPartClick(): void {
    const modalRef: NgbModalRef = this.modalService.open(AddPartComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'add-part-modal-window'
    });

    modalRef.closed.subscribe((result: boolean) => {
      if (result === true) {
        this.refreshInventoryVersion++;
      }
    });
  }

  async onExcelFileSelectedAsync(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    input.value = '';
    if (!file) {
      return;
    }

    let rows: Row[];
    try {
      rows = await readExcelFile(file);
    } catch {
      this.toastr.error(this.transloco.translate('parts.toasts.fileReadError'));
      return;
    }

    const partRows = stripPartsTemplateRows(convertRowsToPartExcelRows(rows, DEFAULT_PARTS_EXCEL_COLUMN_SETTINGS));
    if (partRows.length === 0) {
      this.toastr.error(this.transloco.translate('parts.toasts.noPartRows'));
      return;
    }

    this.openReviewModal(partRows);
  }

  onDownloadTemplateClicked(): void {
    const modalRef = this.modalService.open(DownloadTemplateModalComponent, {
      centered: true,
      windowClass: 'download-template-modal-window'
    });
    const modal = modalRef.componentInstance as DownloadTemplateModalComponent;
    modal.title = this.transloco.translate('parts.downloadTemplate.title');
    modal.subtitle = this.transloco.translate('parts.downloadTemplate.subtitle');
    modal.imageSrc = 'assets/images/parts_excel_template.png';
    modal.imageAlt = this.transloco.translate('parts.downloadTemplate.imageAlt');
    modalRef.closed.subscribe(result => {
      if (result === 'download') {
        void this.onDownloadTemplateAsync();
      }
    });
  }

  async onDownloadTemplateAsync(): Promise<void> {
    if (this.isDownloadingTemplate) return;
    this.isDownloadingTemplate = true;
    try {
      const [makesResponse, modelsResponse, partTypesResponse, categoriesResponse, warehousesResponse, suppliersResponse] = await Promise.all([
        this.lookupService.getMakesAsync(),
        this.lookupService.getAllModelsAsync(),
        this.lookupService.getPartTypesAsync(),
        this.lookupService.getPartCategoriesAsync(),
        this.lookupService.getWarehousesAsync([]),
        this.lookupService.getSuppliersAsync([])
      ]);

      await downloadPartsExcelTemplateAsync({
        makes: makesResponse.data ?? [],
        models: modelsResponse.data ?? [],
        partTypes: (partTypesResponse.data ?? []).map(partType => partType.name),
        categories: (categoriesResponse.data ?? []).map(category => category.name),
        storageLocations: (warehousesResponse.data ?? []).map(warehouse => warehouse.name),
        suppliers: (suppliersResponse.data ?? []).map(supplier => supplier.name)
      });
      this.isDownloadingTemplate = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isDownloadingTemplate = false;
        }
      });
    }
  }

  private openReviewModal(rows: PartExcelRow[]): void {
    const modalRef = this.modalService.open(PartsReviewExcelModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'excel-review-modal-window'
    });
    const modal = modalRef.componentInstance as PartsReviewExcelModalComponent;
    modal.rows = rows;
    modal.createdBy = this.authService.getUser()?.login ?? '';
    modalRef.closed.subscribe(result => {
      if (result === 'imported') {
        this.refreshInventoryVersion++;
        this.syncFilterService();
      }
    });
  }

  private syncFilterService(): void {
    if (!this.isAccountFilterLoaded) {
      return;
    }

    this.filterService.updateFilter({
      accountIds: this.accountFilter.accountIds ?? [],
      ...this.dateFilter
    } as WidgetFilterData);
  }

  onReturnPartsClick(event: MouseEvent) {
    event.stopPropagation();

    const modalRef = this.modalService.open(ReturnPartsComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'return-parts-modal-window'
    });

    modalRef.result
      .then((result: boolean) => {
        if (result === true) {
          this.syncFilterService();
        }
      })
      .catch(() => { });
  }
}